import React, { Fragment, ReactElement, useEffect, useRef, useState } from 'react';

// Components

import SummaryItem from '../SummaryItem';
import MaskUnmaskIcon from '../MaskUnmaskData';

// Utils

import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { receiveSSNs } from '@actions/ssap/ssapActionCreators';
import { socialSecurityNumberTools } from '@utils/tools/socialSecurityNumberTools';
import { MASK_TIMEOUT, maskValue } from '@utils/miscellaneous/mask.utils';


// Types

import { Timeout } from 'react-number-format/types/types';
import { SocialSecurityCard } from '@components/ssap/SsapTypes';

interface ISsnInfo extends SocialSecurityCard {
    idx: number
}


const SsnInfo = (props: ISsnInfo): ReactElement => {
    
    const {
        idx,
        firstNameOnSSNCard,
        lastNameOnSSNCard,
        middleNameOnSSNCard,
        nameSameOnSSNCardIndicator,
        socialSecurityCardHolderIndicator,
        socialSecurityNumber,
        suffixOnSSNCard
    } = props;

    const NameOnSSNCard = {
        firstName: firstNameOnSSNCard,
        lastName: lastNameOnSSNCard,
        middleName: middleNameOnSSNCard,
        suffix: suffixOnSSNCard
    };

    const getBoolean = value => !!value;
    const getNameOnSsnCard = (...args) => {
        const [
            firstName,
            lastName,
            middleName,
            suffix
        ] = args;

        return args.every(v => !v)
            ? 'common.no_text'
            : normalizeNameOfPerson({
                firstName,
                lastName,
                middleName,
                suffix
            });
    };

    const [ssnMasked, setSsnMasked] = useState(true);
    const timeoutRef = useRef<Timeout>(null);

    const getSsnValue = (ssn) => {
        const formattedSSN = socialSecurityNumberTools.formatSSN(ssn);
        return ssnMasked ? maskValue(formattedSSN) : formattedSSN;
    };

    useEffect(() => {
        if (!ssnMasked && !timeoutRef.current) {
            // Start a timeout to reset ssnMasked to true after 15 seconds
            timeoutRef.current = setTimeout(() => {
                setSsnMasked(true);
                timeoutRef.current = null;
            }, MASK_TIMEOUT);
        }

        // Cleanup timeout if ssnMasked changes or component unmounts
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [ssnMasked]);

    return (
        <Fragment>
            <SummaryItem
                textCode = 'common.have_ssn'
                value = { !!socialSecurityNumber }
                desc = {
                    [
                        [
                            `taxHousehold.householdMember[${ idx }].socialSecurityCard.socialSecurityNumber`,
                            getBoolean
                        ]
                    ]
                }
            />

            {
                !!socialSecurityNumber &&

                <SummaryItem
                    textCode = 'common.ssn'
                    icon={
                        <MaskUnmaskIcon
                            receiveData={receiveSSNs}
                            masked={ssnMasked}
                            setMasked={setSsnMasked}
                        />
                    }
                    value={getSsnValue(socialSecurityNumber)}
                    ignoreIntl
                    desc={
                        [
                            [
                                `taxHousehold.householdMember[${idx}].socialSecurityCard.socialSecurityNumber`,
                                getSsnValue
                            ]
                        ]
                    }
                />
            }

            {
                socialSecurityCardHolderIndicator && (
                    <Fragment>
                        <SummaryItem
                            textCode = 'common.name_on_ssn_the_same'
                            value = { nameSameOnSSNCardIndicator }
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].socialSecurityCard.nameSameOnSSNCardIndicator`
                                    ]
                                ]
                            }
                        />

                        {
                            !nameSameOnSSNCardIndicator &&

                            <SummaryItem
                                textCode = 'common.name_as_shown_on_ssn'
                                value = { normalizeNameOfPerson(NameOnSSNCard) }
                                ignoreIntl
                                desc = {
                                    [
                                        [
                                            [
                                                `taxHousehold.householdMember[${ idx }].socialSecurityCard.firstNameOnSSNCard`,
                                                `taxHousehold.householdMember[${ idx }].socialSecurityCard.lastNameOnSSNCard`,
                                                `taxHousehold.householdMember[${ idx }].socialSecurityCard.middleNameOnSSNCard`,
                                                `taxHousehold.householdMember[${ idx }].socialSecurityCard.suffixOnSSNCard`,
                                            ],
                                            getNameOnSsnCard
                                        ]
                                    ]
                                }
                            />

                        }
                    </Fragment>
                )
            }
        </Fragment>
    );
};

export default SsnInfo;